import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {
    invoiceGeneratorActioned,
    BigformActionedAction,
    InvoiceGeneratorActionedField,
} from '@/analytics/segment'
import Button from '@/components/button'
import {Text} from '@/components/text'
import type {InvoiceGeneratorFragment} from '@/graphql-types'
import useFormErrors from '@/hooks/use-form-errors'
import {ArrowLeft} from '@/images/icons/arrow-left'
import {Section} from '@/styles/grid'
import {colors, headerSizes, radii, tokens} from '@/theme'
import {usePDFValues} from '@/utils/html-to-pdf'
import mq from '@/utils/media-query'
import {scrollToTop} from '@/utils/scroll'

import {CompanySection} from './company-section'
import {GetYourInvoiceModal} from './get-your-invoice-modal'
import {InvoiceDetails} from './invoice-details'
import {InvoiceItems} from './invoice-items'
import {NotesAndTerms} from './notes-and-terms'
import {Preview} from './preview'
import {formsPadding} from './shared-styles'
import {Actions, useInvoiceGenerator} from './state'
import {Totals} from './totals'

interface Props {
    content: InvoiceGeneratorFragment['data']
}

export const InvoiceGenerator = ({content}: Props) => {
    const getYourInvoiceModal = content.get_your_invoice_modal[0]
    const thankYouModal = content.thank_you_modal[0]

    const {state, dispatch, isFormValid} = useInvoiceGenerator()
    const [isPreviewVisible, setIsPreviewVisible] = React.useState(false)
    const [isPreviewValidated, setIsPreviewValidated] = React.useState(false)
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = React.useState(false)
    const {previewValues, isPreviewMode} = usePDFValues('invoice-generator', state)
    const {errors, setError, clearError, resetErrors} = useFormErrors(state)

    const onGetYourInvoice = () => {
        if (isFormValid(setError)) {
            setIsInvoiceModalOpen(true)

            invoiceGeneratorActioned({
                action: BigformActionedAction.Interacted,
                field: InvoiceGeneratorActionedField.GetInvoice,
            })
        }
    }

    const onPreview = () => {
        scrollToTop()
        setIsPreviewVisible((isPreviewVisible) => !isPreviewVisible)
        setIsPreviewValidated(isFormValid())

        invoiceGeneratorActioned({
            action: BigformActionedAction.Interacted,
            field: InvoiceGeneratorActionedField.Preview,
        })
    }

    const onReset = () => {
        dispatch({
            type: Actions.RESET_FORM,
        })
        resetErrors()
        setIsInvoiceModalOpen(false)

        invoiceGeneratorActioned({
            action: BigformActionedAction.Interacted,
            field: InvoiceGeneratorActionedField.NewInvoice,
        })
    }

    if (isPreviewMode) {
        return (
            <StyledPreview
                {...previewValues}
                isOpen={true}
                content={content}
                onClosePreview={() => setIsPreviewVisible(false)}
                onGetYourInvoice={() => setIsInvoiceModalOpen(true)}
            />
        )
    }

    return (
        <>
            <Preview
                {...state}
                isOpen={isPreviewVisible}
                content={content}
                onClosePreview={onPreview}
                {...(isPreviewValidated && {onGetYourInvoice: onGetYourInvoice})}
            />
            <Wrapper>
                <Inline>
                    <Text $variant="title-base">{content.title}</Text>
                    <ArrowLeft width={20} style={{transform: 'rotate(270deg)'}} />
                </Inline>

                <InvoiceGeneratorForm noValidate>
                    <Fields>
                        <InvoiceDetails
                            title={content.invoice_details}
                            dispatch={dispatch}
                            state={state}
                            errors={errors}
                            setError={setError}
                            clearError={clearError}
                        />
                        <Companies>
                            <CompanySection
                                dispatch={dispatch}
                                state={state}
                                statePrefix="your"
                                title={content.your_company_form_title}
                                errors={errors}
                                setError={setError}
                                clearError={clearError}
                            />
                            <CompanySection
                                dispatch={dispatch}
                                state={state}
                                statePrefix="recipients"
                                title={content.recipient_company_form_title}
                                errors={errors}
                                setError={setError}
                                clearError={clearError}
                            />
                        </Companies>
                        <InvoiceItems
                            title={content.invoice_items_form_title}
                            dispatch={dispatch}
                            state={state}
                            content={content}
                            errors={errors}
                            setError={setError}
                            clearError={clearError}
                        />
                        <NotesAndTotals>
                            <NotesAndTerms dispatch={dispatch} state={state} content={content} />
                            <Totals
                                dispatch={dispatch}
                                state={state}
                                content={content}
                                errors={errors}
                                setError={setError}
                                clearError={clearError}
                            />
                        </NotesAndTotals>
                    </Fields>
                    <Sidebar>
                        <Button type="button" $variant="outline" onClick={onPreview}>
                            {content.preview_label}
                        </Button>
                        <Button type="button" onClick={onGetYourInvoice}>
                            {content.get_invoice_label}
                        </Button>
                    </Sidebar>
                </InvoiceGeneratorForm>
            </Wrapper>

            <GetYourInvoiceModal
                getYourInvoiceModal={getYourInvoiceModal}
                thankYouModal={thankYouModal}
                isOpen={isInvoiceModalOpen}
                onDismiss={() => setIsInvoiceModalOpen(false)}
                onReset={onReset}
                state={state}
            />
        </>
    )
}

const StyledPreview = styled(Preview)`
    position: relative;
`

const Wrapper = styled(Section)`
    padding-top: ${tokens.spacing40};

    ${mq.min('xl')} {
        padding-right: ${tokens.spacing40};
        padding-left: ${tokens.spacing40};
    }
`

const InvoiceGeneratorForm = styled.form`
    display: grid;
    gap: ${tokens.spacing24};
    margin-top: ${tokens.spacing40};

    ${mq.min('md')} {
        gap: ${tokens.spacing40};
        grid-template-columns: auto max-content;
        align-items: start;
    }
`

const Companies = styled.div`
    display: grid;
    gap: ${tokens.spacing40};

    ${mq.min('xl')} {
        grid-template-columns: 1fr 1fr;
    }
`

const NotesAndTotals = styled.div`
    display: grid;
    gap: ${tokens.spacing40};

    ${mq.min('lg')} {
        grid-template-columns: 1fr 1fr;
    }
`

const Fields = styled.div`
    display: grid;
    gap: ${tokens.spacing40};
`

const Sidebar = styled.div`
    ${formsPadding}
    height: fit-content;
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing20};
    background: ${colors.background.default};
    padding-left: 0;
    padding-right: 0;

    ${mq.min('sm')} {
        flex-direction: row;
    }

    ${mq.min('md')} {
        flex-direction: column;
        padding: ${tokens.spacing40};
        position: sticky;
        top: ${headerSizes.heightLarge}px;
        background: ${colors.background.muted};
        border-radius: ${radii.arc20};
    }
`

const Inline = styled.span`
    display: flex;
    gap: ${tokens.spacing8};
`

export const invoiceGeneratorFragment = graphql`
    fragment InvoiceGenerator on PrismicInvoiceGenerator {
        __typename
        data {
            title
            your_company_form_title
            preview_label
            invoice_details
            get_invoice_label
            recipient_company_form_title
            invoice_items_form_title
            items_label
            price_label
            quantity_label
            rate_label
            total_label
            subtotal_label
            tax_label
            tax_total_label
            notes_label
            terms_label
            add_item_label
            invoice_title
            get_your_invoice_modal {
                modal_title
                modal_subtitle
                modal_image {
                    fluid {
                        base64
                    }
                }
                form_production {
                    formId
                    consents {
                        type
                        communicationConsents {
                            id
                            required
                        }
                    }
                }
                form_staging {
                    formId
                    consents {
                        type
                        communicationConsents {
                            id
                            required
                        }
                    }
                }
            }
            thank_you_modal {
                modal_title
                modal_subtitle
                modal_image {
                    fluid {
                        base64
                    }
                }
                download_pdf_button_text
                new_invoice_button_text
            }
        }
    }
`
